import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Target,
  TrendingUp,
  Activity,
  Flag,
  Sparkles,
  RefreshCw,
  MapPin
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TimelineAcompanhamentoIA = ({ colaboradorId }) => {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [eventos, setEventos] = useState([]);

  useEffect(() => {
    if (colaboradorId) {
      carregarTimeline();
    }
  }, [colaboradorId]);

  const carregarTimeline = async () => {
    try {
      setLoading(true);
      
      // Tentar buscar timeline existente
      try {
        const response = await axios.get(
          `${API}/api/pdi-completo/timeline-ia/${colaboradorId}`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setTimeline(response.data);
        setEventos(response.data.timeline_eventos || []);
      } catch (error) {
        if (error.response?.status === 404) {
          // Não há timeline ainda, gerar nova
          await gerarTimeline();
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
      if (error.response?.status === 404) {
        toast.error('Execute o Diagnóstico IA primeiro para gerar a timeline.');
      } else {
        toast.error('Erro ao carregar timeline');
      }
    } finally {
      setLoading(false);
    }
  };

  const gerarTimeline = async () => {
    try {
      setLoading(true);
      toast.info('Gerando timeline personalizada com IA...', { duration: 3000 });
      
      const response = await axios.post(
        `${API}/api/pdi-completo/timeline-ia?colaborador_id=${colaboradorId}&periodo_meses=12`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setTimeline(response.data);
      setEventos(response.data.timeline_eventos || []);
      
      toast.success(`Timeline criada! ${response.data.timeline_eventos?.length || 0} eventos planejados.`);
    } catch (error) {
      console.error('Erro ao gerar timeline:', error);
      if (error.response?.status === 404) {
        toast.error('Execute o Diagnóstico IA primeiro para gerar a timeline.');
      } else {
        toast.error('Erro ao gerar timeline');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFaseColor = (fase) => {
    const colors = {
      'curto_prazo': 'bg-blue-100 text-blue-800 border-blue-300',
      'medio_prazo': 'bg-purple-100 text-purple-800 border-purple-300',
      'longo_prazo': 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[fase] || colors['curto_prazo'];
  };

  const getFaseLabel = (fase) => {
    const labels = {
      'curto_prazo': 'Curto Prazo (0-3 meses)',
      'medio_prazo': 'Médio Prazo (4-6 meses)',
      'longo_prazo': 'Longo Prazo (7-12 meses)'
    };
    return labels[fase] || fase;
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'acao': <Target className="h-4 w-4" />,
      'marco': <Flag className="h-4 w-4" />,
      'avaliacao': <CheckCircle className="h-4 w-4" />,
      'treinamento': <Activity className="h-4 w-4" />
    };
    return icons[tipo] || icons['acao'];
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'acao': 'bg-blue-100 text-blue-800',
      'marco': 'bg-purple-100 text-purple-800',
      'avaliacao': 'bg-green-100 text-green-800',
      'treinamento': 'bg-orange-100 text-orange-800'
    };
    return colors[tipo] || colors['acao'];
  };

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'alta': 'bg-red-100 text-red-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'baixa': 'bg-gray-100 text-gray-800'
    };
    return colors[prioridade] || colors['media'];
  };

  const calcularDiasRestantes = (dataStr) => {
    const hoje = new Date();
    const data = new Date(dataStr);
    const diff = Math.ceil((data - hoje) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Agrupar eventos por fase
  const eventosPorFase = {
    curto_prazo: eventos.filter(e => e.fase === 'curto_prazo'),
    medio_prazo: eventos.filter(e => e.fase === 'medio_prazo'),
    longo_prazo: eventos.filter(e => e.fase === 'longo_prazo')
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Timeline de Desenvolvimento</h3>
                <p className="text-sm text-gray-600 font-normal">
                  Cronograma personalizado gerado por IA
                </p>
              </div>
            </div>
            <Button 
              onClick={gerarTimeline} 
              disabled={loading}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Gerando...' : 'Atualizar'}
            </Button>
          </CardTitle>
        </CardHeader>
        
        {timeline && (
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">
                  {eventos.length}
                </div>
                <div className="text-sm text-gray-600">Eventos Totais</div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-600">
                  {timeline.fases?.length || 0}
                </div>
                <div className="text-sm text-gray-600">Fases</div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-green-600">
                  {timeline.periodo_meses || 12}
                </div>
                <div className="text-sm text-gray-600">Meses Planejados</div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Fases e Eventos */}
      {timeline && (
        <>
          {['curto_prazo', 'medio_prazo', 'longo_prazo'].map((faseKey) => {
            const eventosNaFase = eventosPorFase[faseKey];
            if (eventosNaFase.length === 0) return null;

            const faseInfo = timeline.fases?.find(f => f.nome.toLowerCase().includes(faseKey.split('_')[0]));

            return (
              <Card key={faseKey} className={`border-2 ${getFaseColor(faseKey)}`}>
                <CardHeader className={getFaseColor(faseKey)}>
                  <CardTitle className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5" />
                    <span>{getFaseLabel(faseKey)}</span>
                    <Badge variant="outline">{eventosNaFase.length} eventos</Badge>
                  </CardTitle>
                  {faseInfo && (
                    <div className="text-sm font-normal mt-2">
                      <p><strong>Foco:</strong> {faseInfo.foco_principal}</p>
                      {faseInfo.objetivos && faseInfo.objetivos.length > 0 && (
                        <div className="mt-2">
                          <strong>Objetivos:</strong>
                          <ul className="list-disc list-inside ml-2">
                            {faseInfo.objetivos.map((obj, i) => (
                              <li key={i}>{obj}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-6">
                  <div className="relative">
                    {/* Linha vertical */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

                    <div className="space-y-4">
                      {eventosNaFase.map((evento, idx) => {
                        const diasRestantes = calcularDiasRestantes(evento.data_sugerida);
                        const isUrgente = diasRestantes <= 30 && diasRestantes > 0;

                        return (
                          <div key={idx} className="relative flex items-start space-x-4 ml-2">
                            {/* Círculo da timeline */}
                            <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 border-white bg-blue-500 shadow">
                              {getTipoIcon(evento.tipo)}
                            </div>

                            {/* Card do Evento */}
                            <Card className={`flex-1 ${isUrgente ? 'border-2 border-orange-300' : ''}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <h4 className="font-bold text-lg">{evento.titulo}</h4>
                                      <Badge className={getTipoColor(evento.tipo)}>
                                        {evento.tipo}
                                      </Badge>
                                      {evento.prioridade && (
                                        <Badge className={getPrioridadeColor(evento.prioridade)}>
                                          {evento.prioridade}
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    <p className="text-gray-600 mb-3">{evento.descricao}</p>
                                    
                                    {evento.competencia_relacionada && (
                                      <div className="bg-purple-50 rounded-lg p-3 mb-3">
                                        <div className="flex items-center space-x-2">
                                          <Target className="h-4 w-4 text-purple-600" />
                                          <span className="text-sm font-semibold text-purple-900">
                                            Competência: {evento.competencia_relacionada}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                      <div>
                                        <span className="text-gray-500">Data Sugerida:</span>
                                        <div className="font-semibold">
                                          {new Date(evento.data_sugerida).toLocaleDateString('pt-BR')}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Dias Restantes:</span>
                                        <div className={`font-semibold ${
                                          diasRestantes < 0 ? 'text-red-600' : 
                                          isUrgente ? 'text-orange-600' : 'text-green-600'
                                        }`}>
                                          {diasRestantes < 0 
                                            ? `${Math.abs(diasRestantes)} dias atrás` 
                                            : `${diasRestantes} dias`}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Duração Estimada:</span>
                                        <div className="font-semibold text-blue-600">
                                          {evento.duracao_estimada_dias} dias
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {evento.resultados_esperados && evento.resultados_esperados.length > 0 && (
                                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="text-sm font-semibold text-blue-900 mb-2">
                                          Resultados Esperados:
                                        </div>
                                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                                          {evento.resultados_esperados.map((resultado, i) => (
                                            <li key={i}>{resultado}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {evento.dependencias && evento.dependencias.length > 0 && (
                                      <div className="mt-2 text-xs text-gray-600">
                                        <strong>Dependências:</strong> {evento.dependencias.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      {/* Marcos Principais */}
      {timeline?.marcos_principais && timeline.marcos_principais.length > 0 && (
        <Card className="border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle className="flex items-center space-x-2">
              <Flag className="h-5 w-5 text-green-600" />
              <span>Marcos Principais</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ul className="space-y-2">
              {timeline.marcos_principais.map((marco, i) => (
                <li key={i} className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-gray-700">{marco}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Recomendações Gerais */}
      {timeline?.recomendacoes_gerais && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center space-x-2 text-base">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <span>Recomendações da IA</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-gray-700">{timeline.recomendacoes_gerais}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !timeline && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-12 text-center">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Timeline de Desenvolvimento com IA
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Clique em "Atualizar" para que a IA analise seu diagnóstico PDI e crie um 
              cronograma personalizado de desenvolvimento para os próximos 12 meses
            </p>
            <Button 
              onClick={gerarTimeline}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Gerar Timeline com IA
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TimelineAcompanhamentoIA;
